<template>
  <div
    id="suitup"
    class="content resizable draggable-window hidden"
  >
    <div class="control-box close-box">
      <a class="control-box-inner" />
    </div>
    <div class="control-box zoom-box">
      <div class="control-box-inner">
        <div class="zoom-box-inner" />
      </div>
    </div>
    <div class="control-box window-collapse">
      <div class="control-box-inner">
        <div class="window-collapse-inner" />
      </div>
    </div>
    <h1 class="titlebar">
      <span>{{ punk ? `Suit Up: ${punk.name}` : 'Suit Up' }}</span>
    </h1>
    <div class="inner">
      <form
        class="suit-form"
        @submit.prevent="suitUp(punkNumber)"
      >
        <label for="suit-punk-number">Punk #</label>
        <input
          id="suit-punk-number"
          v-model="punkNumber"
          type="number"
          min="0"
          :max="MAX_PUNK_ID"
        >
        <button
          class="command_button"
          type="submit"
          :disabled="loading"
        >
          {{ loading ? 'Suiting up...' : 'Suit Up' }}
        </button>
        <button
          class="command_button"
          type="button"
          :disabled="loading"
          @click="suitUpRandom"
        >
          Random
        </button>
      </form>

      <p
        v-if="error"
        class="punks-status"
      >
        {{ error }}
      </p>
      <p
        v-else-if="!punk && !loading"
        class="punks-status"
      >
        Enter a punk number to see it in its radioactive suit.
      </p>
      <LoadingSpinner v-else-if="!punk" />

      <div
        v-if="punk"
        class="suit-body"
      >
        <div
          v-if="walkSheet"
          class="suit-tabs"
        >
          <button
            class="command_button"
            :class="{ active: view === 'suit' }"
            @click="view = 'suit'"
          >
            Portrait
          </button>
          <button
            class="command_button"
            :class="{ active: view === 'walk' }"
            @click="view = 'walk'"
          >
            Walkcycle
          </button>
        </div>

        <div
          v-if="view === 'suit'"
          class="suit-stage"
        >
          <img
            :src="layers.bg"
            alt=""
          >
          <img
            class="suit-punk"
            :src="punk.image"
            :style="punkStyle"
            :alt="punk.name"
          >
          <img
            :src="layers.suit"
            alt=""
          >
          <img
            :src="layers.visor"
            alt=""
          >
          <img
            :src="layers.glow"
            alt=""
          >
        </div>
        <div
          v-else
          class="suit-stage suit-stage-walk"
        >
          <div
            class="suit-walker"
            :class="`suit-walker-${walkDirection}`"
            :style="{ backgroundImage: `url(${walkSheet})` }"
          />
        </div>

        <div
          v-if="view === 'walk'"
          class="suit-directions"
        >
          <button
            v-for="direction in WALK_DIRECTIONS"
            :key="direction.name"
            class="command_button"
            :class="{ active: walkDirection === direction.name }"
            :aria-label="`Walk ${direction.name}`"
            :title="`Walk ${direction.name}`"
            @click="walkDirection = direction.name"
          >
            {{ direction.arrow }}
          </button>
        </div>

        <div class="punk-downloads">
          <button
            class="command_button"
            :disabled="!!exporting"
            @click="download('png')"
          >
            {{ exporting === 'png' ? 'Saving...' : 'Download PNG' }}
          </button>
          <button
            class="command_button"
            :disabled="!!exporting"
            @click="download('gif')"
          >
            {{ exporting === 'gif' ? 'Making GIF...' : 'Download GIF' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RADIOACTIVEPUNKS from '@/assets/RADIOACTIVEPUNKS.json'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { downloadBlob } from '@/punkImage'
import {
  SUIT_LAYOUT,
  renderSuitedGIF,
  renderSuitedPNG,
  renderWalkGIF,
  renderWalkPNG,
  suitGlowFor,
  suitLayerUrl
} from '@/suitImage'
import { RPUNKS_ADDRESS, fetchPunkMetadata, read } from '@/web3'

const rpunks = { address: RPUNKS_ADDRESS, abi: RADIOACTIVEPUNKS.abi }
const MAX_PUNK_ID = 2999
const WALK_DIRECTIONS = [
  { name: 'left', arrow: '←' },
  { name: 'up', arrow: '↑' },
  { name: 'down', arrow: '↓' },
  { name: 'right', arrow: '→' }
]

const percentOfSuit = value => `${(value / SUIT_LAYOUT.size) * 100}%`

// walk sheets only exist for some punks. a missing one also fails here on hosts that answer with index.html
function walkSheetExists (src) {
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = src
  })
}

// dresses any punk in its radioactive suit. read-only and off-chain, so no wallet is needed
export default {
  name: 'SuitUpWindow',
  components: {
    LoadingSpinner
  },
  data () {
    return {
      MAX_PUNK_ID,
      WALK_DIRECTIONS,
      walkDirection: 'right',
      punkNumber: '',
      punk: undefined,
      walkSheet: '',
      view: 'suit',
      loading: false,
      error: '',
      exporting: '',
      requestId: 0
    }
  },
  computed: {
    layers () {
      return {
        bg: suitLayerUrl('bg'),
        suit: suitLayerUrl('suit'),
        visor: suitLayerUrl('visor-old'),
        glow: suitLayerUrl(suitGlowFor(this.punk.attributes))
      }
    },
    punkStyle () {
      const { x, y, size } = SUIT_LAYOUT.punk
      return { left: percentOfSuit(x), top: percentOfSuit(y), width: percentOfSuit(size), height: percentOfSuit(size) }
    }
  },
  methods: {
    async suitUp (number) {
      const id = Number(number)
      if (number === '' || !Number.isInteger(id) || id < 0 || id > MAX_PUNK_ID) {
        this.error = `Enter a punk number from 0 to ${MAX_PUNK_ID}.`
        return
      }

      // the newest request wins if an older one is still in flight
      const requestId = ++this.requestId
      this.punkNumber = id
      this.loading = true
      this.error = ''
      try {
        const walkSheet = `${process.env.BASE_URL}walkcycles/${id}.png`
        const [data, hasWalkSheet] = await Promise.all([fetchPunkMetadata(id), walkSheetExists(walkSheet)])
        if (requestId !== this.requestId) { return }
        this.punk = { id, ...data }
        this.walkSheet = hasWalkSheet ? walkSheet : ''
        this.view = 'suit'
      } catch (e) {
        console.warn(e)
        if (requestId === this.requestId) {
          this.punk = undefined
          this.error = `Punk #${id} could not be found.`
        }
      } finally {
        if (requestId === this.requestId) { this.loading = false }
      }
    },

    async suitUpRandom () {
      this.loading = true
      this.error = ''
      try {
        const supply = await read({ ...rpunks, functionName: 'totalSupply' })
        const index = BigInt(Math.floor(Math.random() * Number(supply)))
        const id = await read({ ...rpunks, functionName: 'tokenByIndex', args: [index] })
        await this.suitUp(Number(id))
      } catch (e) {
        console.warn(e)
        this.error = 'Couldn\'t find a random punk. Try again.'
        this.loading = false
      }
    },

    // exports whatever is on screen: the suited punk, or its walk in the current direction
    async download (format) {
      this.exporting = format
      try {
        const gif = format === 'gif'
        if (this.view === 'walk') {
          const render = gif ? renderWalkGIF : renderWalkPNG
          const blob = await render(this.walkSheet, this.walkDirection)
          downloadBlob(blob, `radioactive-punk-${this.punk.id}-walk-${this.walkDirection}.${format}`)
        } else {
          const render = gif ? renderSuitedGIF : renderSuitedPNG
          const blob = await render(this.punk.image, this.punk.attributes)
          downloadBlob(blob, `radioactive-punk-${this.punk.id}-suited.${format}`)
        }
      } catch (e) {
        console.warn(e)
        window.alert(`Couldn't make a ${format.toUpperCase()} of this punk.`)
      } finally {
        this.exporting = ''
      }
    }
  }
}
</script>

<style lang="scss">
$walk-cell: 78px;
$walk-scale: 4;

#suitup {
  left: 120px;
  top: 40px;

  @media (max-width: 800px) {
    left: 10px;
  }

  .inner {
    width: 380px;
    height: 520px;

    @media (max-width: 800px) {
      width: 300px;
    }
  }
}

.suit-form {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;

  input {
    width: 70px;
    min-width: 0;
  }

  button {
    padding: 3px 8px;
  }
}

.suit-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.suit-tabs,
.suit-directions {
  display: flex;
  gap: 6px;

  button {
    flex: 1;
    padding: 3px 0;

    &.active {
      background: #3eae2e;
      color: #fff;
    }
  }
}

.suit-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border: 1px solid #111;
  background: #1e2d3b;

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    image-rendering: pixelated;
  }
}

.suit-stage-walk {
  display: flex;
  align-items: center;
  justify-content: center;
}

// sheets are 16x8 cells; the first four cells of the first four rows walk right, up, left and down
.suit-walker {
  flex-shrink: 0;
  width: $walk-cell * $walk-scale;
  height: $walk-cell * $walk-scale;
  background-size: ($walk-cell * 16 * $walk-scale) ($walk-cell * 8 * $walk-scale);
  image-rendering: pixelated;
  animation: suit-walk 0.6s steps(4) infinite;
}

.suit-walker-right {
  background-position-y: 0;
}

.suit-walker-up {
  background-position-y: -$walk-cell * 1 * $walk-scale;
}

.suit-walker-left {
  background-position-y: -$walk-cell * 2 * $walk-scale;
}

.suit-walker-down {
  background-position-y: -$walk-cell * 3 * $walk-scale;
}

@keyframes suit-walk {
  from {
    background-position-x: 0;
  }

  to {
    background-position-x: -$walk-cell * 4 * $walk-scale;
  }
}
</style>
